/**
 * Utility functions for working with movement data
 */

/**
 * Groups transactions by date
 */
export function groupTransactionsByDate(transactions) {
  return transactions.reduce((acc, transaction) => {
    const date = transaction.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(transaction);
    return acc;
  }, {});
}

/**
 * Extracts unique dates from grouped transactions and sorts them
 */
export function getUniqueSortedDates(groupedTransactions) {
  return Object.keys(groupedTransactions).sort().reverse();
}

/**
 * Calculates summary statistics for a list of movements
 */
export function calculateMovementSummaries(movements) {
  // Group expenses by currency
  const expensesByCurrency = movements
    .filter(item => item.type === "Expenses")
    .reduce((acc, transaction) => {
      const currency = transaction.currency;
      if (!acc[currency]) {
        acc[currency] = [];
      }
      acc[currency].push(transaction);
      return acc;
    }, {});

  // Group income by currency
  const incomeByCurrency = movements
    .filter(item => item.type === "Income")
    .reduce((acc, transaction) => {
      const currency = transaction.currency;
      if (!acc[currency]) {
        acc[currency] = [];
      }
      acc[currency].push(transaction);
      return acc;
    }, {});

  // Sum expenses by currency
  const sumExpensesByCurrency = {};
  for (const currency in expensesByCurrency) {
    sumExpensesByCurrency[currency] = expensesByCurrency[currency].reduce(
      (sum, expense) => sum + expense.amount, 
      0
    );
  }

  // Sum income by currency
  const sumIncomeByCurrency = {};
  for (const currency in incomeByCurrency) {
    sumIncomeByCurrency[currency] = incomeByCurrency[currency].reduce(
      (sum, income) => sum + income.amount, 
      0
    );
  }

  // Calculate net balance by currency
  const netBalance = {};
  const allCurrencies = new Set([
    ...Object.keys(sumExpensesByCurrency),
    ...Object.keys(sumIncomeByCurrency)
  ]);

  for (const currency of allCurrencies) {
    const expenses = sumExpensesByCurrency[currency] || 0;
    const income = sumIncomeByCurrency[currency] || 0;
    netBalance[currency] = income - expenses;
  }

  return {
    expensesByCurrency: sumExpensesByCurrency,
    incomeByCurrency: sumIncomeByCurrency,
    netBalance
  };
}

export function getMissingFieldsMessage(movement) {
  if (!movement) return null;
  if (movement.type === 'Transfer') {
    const noFrom = !movement.from_account_id;
    const noTo = !movement.to_account_id;
    if (noFrom && noTo) return null;
    if (noFrom) return 'This transfer has no origin account. Do you want to continue?';
    if (noTo) return 'This transfer has no destination account. Do you want to continue?';
    return null;
  }
  const noCategory = !movement.category_id;
  const noAccount = !movement.account_id;
  if (noCategory && noAccount) return 'This movement has no category and no account. Do you want to continue?';
  if (noCategory) return 'This movement has no category. Do you want to continue?';
  if (noAccount) return 'This movement has no account. Do you want to continue?';
  return null;
}
